import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../hooks/use-auth';
import { useToasts } from '../../hooks/use-toasts';
import { isValidEmail, isValidPassword } from '../../utils/validators';

export const useRegister = () => {
  const [values, setValues] = useState<{
    email: string;
    password: string;
    rememberMe: boolean;
  }>({
    email: '',
    password: '',
    rememberMe: true,
  });
  const [errors, setErrors] = useState<{
    email: string | boolean;
    password: string | boolean;
  }>({
    email: false,
    password: false,
  });
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [acceptedTOS, setAcceptedTOS] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { signup, updatePersistence } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToasts();

  const onChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setValues({ ...values, [name]: value });
  };

  const onToggleRememberMe = () => {
    setValues({ ...values, rememberMe: !values.rememberMe });
  };

  const onToggleTOS = () => {
    setAcceptedTOS(!acceptedTOS);
  };

  useEffect(() => {
    if (hasSubmitted) validateEmail();
  }, [values.email]);

  useEffect(() => {
    if (hasSubmitted) validatePassword();
  }, [values.password]);

  const validateEmail = () => {
    let error: string | boolean = false;
    if (!isValidEmail(values.email)) error = 'Invalid email.';
    setErrors((errors) => ({ ...errors, email: error }));
  };

  const validatePassword = () => {
    let error: string | boolean = false;
    if (!isValidPassword(values.password)) error = 'Must be 6 or more characters.';
    setErrors((errors) => ({ ...errors, password: error }));
  };

  const onSubmit = async () => {
    setHasSubmitted(true);
    validateEmail();
    validatePassword();
    if (acceptedTOS && isValidPassword(values.password) && isValidEmail(values.email)) {
      setLoading(true);
      try {
        await updatePersistence(values.rememberMe);
        await signup(values.email, values.password);
        navigate('/maps');
      } catch (error: any) {
        if (!error) error = { message: 'An unknown error occured.' };

        const emailAlreadyInUse = error.code == 'auth/email-already-in-use';
        if (emailAlreadyInUse) {
          // show error on the password field
          setErrors({ ...errors, email: error.message });
        } else {
          showToast({
            severity: 'error',
            summary: error.message || `An unknown error occured [${error.code}].`,
            detail: `Error code [${error.code || 'unknown'}]`,
            sticky: true,
          });
        }
      }
      setLoading(false);
    }
  };

  const navigateLogin = () => {
    navigate('/login');
  };

  return {
    values,
    errors,
    onSubmit,
    hasSubmitted,
    loading,
    onChange,
    onToggleRememberMe,
    onToggleTOS,
    navigateLogin,
    acceptedTOS,
  };
};
